import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Router } from '@angular/router';

import 'firebase/compat/auth';

import { AuthorizationError, DuplicatePutError, ErrorContext, ServerError } from '../models/errors/error.model';
import { Organization } from '../models/organization/organization.model';
import { OrganizationService } from '../models/organization/organization.service';
import { User } from '../models/user/user.model';
import { UserService } from '../models/user/user.service';
import { MerchantsState } from '../state-management/merchants/merchants.state';
import { ProposalsState } from '../state-management/proposals/proposals.state';
import { StatementsState } from '../state-management/statements/statements.state';
import { ApiHelperService } from './api-helper.service';
import { StateService } from './state.service';
import { PUBLIC_ORGANIZATION_ID } from '../constants';
import { environment } from '../../environments/environment';

class UserResetLinkError extends ServerError {
    constructor(msg: string, ctx?: ErrorContext) {
        super(msg, ctx);
        this.ctx.userMessage = 'We were unable to send the reset link. Support has been notified.';
    }
}

@Injectable({
    providedIn: 'root'
})
export class AuthService {

    constructor(
        public afAuth: AngularFireAuth,
        private api: ApiHelperService,
        private userService: UserService,
        private organizationService: OrganizationService,
        public router: Router,
        private state: StateService,
        private merchantsState: MerchantsState,
        private proposalsState: ProposalsState,
        private statementsState: StatementsState
    ) { }

    async bootstrapSession() {

        console.log("bootstrapSession(): Bootstrapping session");
        // return;

        return await this.userService.bootstrapSession()
            .then(async (user: User) => {
                console.log("User bootstrapped: ", user);
                await this.login2(user);
            })
            .catch(async e => {
                console.log("Error bootstrapping session: ", e);
                if (e instanceof AuthorizationError) {
                    throw e;
                }
                e.message += '\nin auth.bootstrapSession: Could not bootstrapSession';
                await this.api.reportAndRethrow(e);
            });

    }

    async login2(user: User) {
        const promises: Promise<unknown>[] = [
            this.userService.patchUser(user.id!, { lastSignInTime: new Date() })
                .catch(async e => {
                    e.message += '\nIn auth.login: Could not patchUser';
                    await this.api.reportAndRethrow(e);
                })
        ];

        if (user.organizationId != null) {
            promises.push(this.organizationService.getOrganizationById(user.organizationId)
                .catch(async e => {
                    e.message += '\nIn auth.login: Could not getOrganizationById';
                    await this.api.reportAndRethrow(e);
                })
            );
        }

        const values = await Promise.all(promises);
        const sessionUser = values[0] as User;
        this.state.setUser(sessionUser);
        if (values.length > 1) {
            this.state.setOrganization(values[1] as Organization);
        }

        await this.completeInvitedUserIfReady(sessionUser);
    }

    async redirectAfterLogin() {
        const user = await this.state.getUser();

        if (user?.organizationId === PUBLIC_ORGANIZATION_ID) {
            // Account exists but they haven't created a real organization/
            // billing setup yet — force them there instead of wherever they'd
            // normally land.
            return this.router.navigate(['manage', 'account-setup']);
        }

        return this.router.navigate(['manage', user?.redirectPage ?? '']);
    }

    updateUserProfile(profile) {
        return this.state.firebaseUser.updateProfile(profile)
            .catch(async e => {
                e.message += '\nin auth.updateUserProfile: Could not updateProfile';
                await this.api.reportAndRethrow(e);
            });
        ;
    }

    refreshUser() {
        return this.state.firebaseUser.getIdToken(true);
    }

    isAuthenticated(): boolean {
        return this.state.loggedIn;
    }

    isAnonymous(): boolean {
        // Any non-logged in user is anonymous.
        return this.state.firebaseUser?.isAnonymous ?? true;
    }

    /**
     * Checks a userid to see if they can access a particular
     * thing.
     * see: select name from auth.access_bit_names; for the full list
     */
    async checkAccess(accessBitName: string | string[], userid: string): Promise<Record<string, boolean>> {
        return this.api.apiCallSane(
            '/auth/check-access',
            'GET',
            this.api.getDefaultHeaders(),
            { accessBitName, userid },
            null,
            { authenticate: false, setRoot: true }
        );
    }

    async logout() {
        await this.endSession();
        this.redirectToSuiteSignIn();
    }

    async sessionExpired(): Promise<void> {
        await this.endSession();
        this.redirectToSuiteSignIn();
    }

    redirectToSuiteSignIn(): void {
        window.location.href = environment.suiteUrl + '/sign-in';
    }

    private async endSession(): Promise<void> {
        try {
            await this.userService.signOutSession();
        } catch {
            // still clear local auth state
        }
        try {
            await this.afAuth.signOut();
        } catch {
            // ignore
        }
        this.clearLocalSession();
    }

    private clearLocalSession(): void {
        this.state.purge();
        this.merchantsState.resetState();
        this.proposalsState.resetState();
        this.statementsState.resetState();
    }

    /**
     * Invited users reset password in Suite; skip Audit invited-setup entirely.
     */
    private async completeInvitedUserIfReady(user: User): Promise<void> {
        if (!user.isFromInvite || user.inviteCompleted) {
            return;
        }

        const firstName = user.firstName?.trim() ?? '';
        const lastName = user.lastName?.trim() ?? '';

        try {
            const { user: updatedUser, organization } = await this.userService.completeInvite(
                user.id!,
                {
                    firstName,
                    lastName,
                    termsAccepted: true,
                    isFreeTrialUser: user.isFreeTrialAccount ?? false,
                }
            );
            await this.state.updateUser(updatedUser);
            if (organization) {
                await this.state.updateOrganization(organization);
            }
        } catch (e) {
            if (e instanceof DuplicatePutError) {
                return;
            }
            console.warn('completeInvitedUserIfReady failed', e);
        }
    }
}
